// ============================================================================
// MembersTab — onglet MEMBRES de l'ecran Admin (AD-C : identite, la surface
// sensible de la vague)
// ============================================================================
// Porte du PoC components/admin/members-section.tsx + member-row.tsx aux
// standards du parc : DataTable @gs/ui, dialogs GsWizardDialog (AD-0).
// DIVERGENCES TRANCHEES :
//   P-ADM-14 : la recherche est un ETAT REACT + filtrage standard — on ne porte
//   PAS le hack DOM du PoC (members-search.tsx manipulait document.getElementById
//   / row.style.display avec des IDs magiques).
//   STOP-4a : le role est un BADGE non editable (« gere via la Console UG ») —
//   derive Systeme C + qualite director derivee de gest.Teams (STOP-8).
//   STOP-5 : aucun bloc « changer le courriel » nulle part (pas de bouton mort).
//   P-ADM-18 : le toggle actif/inactif bascule le statut d'EMPLOI (is_employed),
//   date de depart obligatoire a la desactivation (MemberStatusDialog).

import { useCallback, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Check, Eye, Plus, UserMinus, Users, UserX, UserCheck } from 'lucide-react';
import {
  DataTable, EmptyState, SkeletonCard, QueryError, GsSearchInput, GsSegmented, GS_COMPONENTS, type Column,
} from '@gs/ui';
import { useTranslation } from '../../i18n';
import {
  useCreateMember, useUpdateMember, useSetMemberEmployed, useDecideInactivation,
  adminMemberErrorCode, effectiveMemberRole,
  type AdminMember, type AdminMembersResponse, type AdminInactivationRequest,
  type MemberCreatePayload, type MemberUpdatePayload,
} from '../../hooks/useAdminMembers';
import type { GestAdminRole } from '../../hooks/useAdminAudit';
import { formatDateOnlyFr, formatDateTimeFr } from '../../lib/format/date';
import { personRoleI18nKey } from '../../lib/org/derivePersonRole';
import { SectionHeader } from './SectionHeader';
import { MemberFormDialog, initialMemberValues, type MemberFormValues } from './MemberFormDialog';
import { MemberStatusDialog } from './MemberStatusDialog';
import {
  filterMembersBySearch,
  filterMembersByStatus,
  parseMemberStatusFilter,
  type MemberStatusFilter,
} from '../../lib/admin/member-filters';

// `initial` est FIGE au moment du clic (objet stable — meme motif que TeamsTab).
type DialogState =
  | { mode: 'create' }
  | { mode: 'edit'; member: AdminMember; initial: MemberFormValues }
  | null;

const norm = (s: string): string => s.toLowerCase().trim();

export function MembersTab({
  isLoading,
  isError,
  data,
  onRetry,
}: {
  isLoading: boolean;
  isError: boolean;
  data: AdminMembersResponse | undefined;
  onRetry: () => void;
}) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const createMember = useCreateMember();
  const updateMember = useUpdateMember();
  const setEmployed = useSetMemberEmployed();
  const decide = useDecideInactivation();

  const [search, setSearch] = useState('');
  const statusFilter = parseMemberStatusFilter(searchParams.get('status'));
  const [dialog, setDialog] = useState<DialogState>(null);
  const [statusFor, setStatusFor] = useState<AdminMember | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const setStatusFilter = useCallback(
    (next: MemberStatusFilter) => {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', 'members');
        if (next === 'active') params.delete('status');
        else params.set('status', next);
        return params;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // P-ADM-14 : filtres PURS (etat React + URL ?status=), memoises — jamais de DOM.
  const filtered = useMemo(() => {
    if (!data) return [];
    const byStatus = filterMembersByStatus(data.members, statusFilter);
    return filterMembersBySearch(byStatus, search);
  }, [data, search, statusFilter]);

  const directorEmails = useMemo(
    () =>
      new Set(
        (data?.teams ?? [])
          .map((tm) => tm.directorEmail && norm(tm.directorEmail))
          .filter((e): e is string => !!e),
      ),
    [data],
  );

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-4">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    );
  }
  if (isError || !data) {
    return <QueryError message={t('gest:admin.members.loadError')} onRetry={onRetry} />;
  }

  const teamNames = new Map(data.teams.map((tm) => [tm.id, tm.name]));
  const pending = createMember.isPending || updateMember.isPending || setEmployed.isPending;

  // Candidats responsables : membres EN EMPLOI + toute personne encore assignee
  // (preserve la valeur courante d'un select — parite PoC members-section).
  const stillAssigned = new Set(
    data.members.map((m) => m.managerEmail && norm(m.managerEmail)).filter((e): e is string => !!e),
  );
  const managerOptions = data.members
    .filter((m) => m.isEmployed || stillAssigned.has(norm(m.email)))
    .map((m) => ({ value: m.email, label: m.fullName ?? m.email }))
    .sort((a, b) => a.label.localeCompare(b.label, 'fr'));

  const done = {
    onSuccess: () => {
      setDialog(null);
      setStatusFor(null);
    },
    onError: (err: unknown) => {
      setDialog(null);
      setStatusFor(null);
      setErrorCode(adminMemberErrorCode(err));
    },
  };

  function submitCreate(payload: MemberCreatePayload) {
    setErrorCode(null);
    createMember.mutate(payload, done);
  }
  function submitUpdate(payload: MemberUpdatePayload) {
    if (dialog?.mode !== 'edit') return;
    setErrorCode(null);
    updateMember.mutate({ email: dialog.member.email, payload }, done);
  }
  function submitStatus(input: { employed: boolean; departureDate: string | null }) {
    if (!statusFor) return;
    setErrorCode(null);
    setEmployed.mutate({ email: statusFor.email, ...input }, done);
  }

  const roleFor = (m: AdminMember): GestAdminRole => effectiveMemberRole(m, directorEmails);

  const columns: Column<AdminMember>[] = [
    {
      key: 'name',
      header: t('gest:admin.members.columns.name'),
      render: (m) => (
        <div className={`min-w-0 ${m.isEmployed ? '' : 'opacity-60'}`}>
          <div className="flex items-center gap-2">
            <p className="truncate font-semibold text-slate-900 dark:text-slate-100">
              {m.fullName ?? m.email}
            </p>
            {!m.isEmployed ? (
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-chip font-medium uppercase text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                {t('gest:admin.members.badge.notEmployed')}
              </span>
            ) : null}
          </div>
          {m.matricule ? (
            <p className="truncate text-meta text-slate-500 dark:text-slate-400">#{m.matricule}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'email',
      header: t('gest:admin.members.columns.email'),
      render: (m) => <span className="text-slate-500 dark:text-slate-400">{m.email}</span>,
    },
    {
      key: 'role',
      // STOP-4a : badge AFFICHE, jamais editable (Console UG).
      header: t('gest:admin.members.columns.role'),
      render: (m) => (
        <span
          title={t('gest:admin.members.roleHint')}
          className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-chip font-medium uppercase text-slate-600 dark:bg-slate-800 dark:text-slate-300"
        >
          {t(personRoleI18nKey(roleFor(m)))}
        </span>
      ),
    },
    {
      key: 'committee',
      header: t('gest:admin.members.columns.committee'),
      render: (m) =>
        m.committee === 'ce' || m.committee === 'cg' ? (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-chip font-medium uppercase text-blue-700 dark:bg-blue-950 dark:text-blue-300">
            {t(`gest:profile.committees.${m.committee}`)}
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">{'—'}</span>
        ),
    },
    {
      key: 'team',
      header: t('gest:admin.members.columns.team'),
      render: (m) =>
        (m.teamId && teamNames.get(m.teamId)) ?? (
          <span className="text-slate-400 dark:text-slate-500">
            {t('gest:admin.members.noTeam')}
          </span>
        ),
    },
    {
      key: 'actions',
      header: t('gest:admin.members.columns.actions'),
      className: 'text-right',
      render: (m) => {
        const name = m.fullName ?? m.email;
        return (
          <div className="inline-flex items-center gap-1">
            <Link
              to={`/profile/${encodeURIComponent(m.email)}?from=admin`}
              aria-label={t('gest:admin.members.viewProfile', { name })}
              onClick={(e) => e.stopPropagation()}
              className="inline-flex h-8 w-8 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <Eye size={16} aria-hidden="true" />
            </Link>
            <button
              type="button"
              aria-label={
                m.isEmployed
                  ? t('gest:admin.members.status.deactivateTitle', { name })
                  : t('gest:admin.members.status.activateTitle', { name })
              }
              onClick={(e) => {
                e.stopPropagation();
                setErrorCode(null);
                setStatusFor(m);
              }}
              className="inline-flex h-8 w-8 touch-manipulation items-center justify-center rounded-lg text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              {m.isEmployed ? <UserX size={16} aria-hidden="true" /> : <UserCheck size={16} aria-hidden="true" />}
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex flex-col gap-5">
      <SectionHeader
        title={t('gest:admin.members.title')}
        description={t('gest:admin.members.description')}
      >
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.members.countShown', { n: filtered.length, total: data.members.length })}
        </span>
        <button
          type="button"
          onClick={() => { setErrorCode(null); setDialog({ mode: 'create' }); }}
          className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5`}
        >
          <Plus size={14} aria-hidden="true" />
          {t('gest:admin.members.create')}
        </button>
      </SectionHeader>

      {errorCode ? (
        <p role="alert" className="text-field text-red-600 dark:text-red-400">
          {t(`gest:admin.members.errors.${errorCode}`)}
        </p>
      ) : null}

      <InactivationQueue
        requests={data.pending}
        isDeciding={decide.isPending}
        onDecide={(id, decision, decisionNote) => {
          setErrorCode(null);
          decide.mutate({ id, decision, decisionNote }, {
            onError: (err: unknown) => setErrorCode(adminMemberErrorCode(err)),
          });
        }}
      />

      <div className="flex flex-col gap-4 sm:flex-row sm:flex-wrap sm:items-end">
        <div className="flex flex-col gap-1.5">
          <span className="text-meta font-medium text-slate-600 dark:text-slate-300">
            {t('gest:admin.members.filter.statusLabel')}
          </span>
          <GsSegmented
            value={statusFilter}
            onChange={(v) => setStatusFilter(v as MemberStatusFilter)}
            ariaLabel={t('gest:admin.members.filter.statusAria')}
            options={[
              { value: 'active', label: t('gest:admin.members.filter.active') },
              { value: 'inactive', label: t('gest:admin.members.filter.inactive') },
              { value: 'all', label: t('gest:admin.members.filter.all') },
            ]}
          />
        </div>
        {/* P-ADM-14 : recherche = etat React, filtrage pur au rendu. */}
        <label className="flex min-w-0 flex-1 flex-col gap-1.5 sm:max-w-md text-meta font-medium text-slate-600 dark:text-slate-300">
          {t('gest:admin.members.searchLabel')}
          <GsSearchInput
            value={search}
            onChange={setSearch}
            placeholder={t('gest:admin.members.searchPlaceholder')}
            className="w-full font-normal"
          />
        </label>
      </div>

      {data.members.length === 0 ? (
        <EmptyState
          icon={<Users size={48} aria-hidden="true" />}
          title={t('gest:admin.members.empty')}
          description={t('gest:admin.members.emptyHint')}
        />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Users size={48} aria-hidden="true" />}
          title={t('gest:admin.members.noMatch')}
        />
      ) : (
        <div className={`${GS_COMPONENTS.card} overflow-hidden p-0`}>
          <DataTable
            columns={columns}
            data={filtered}
            rowKey={(m) => m.email}
            onRowClick={(m) => {
              setErrorCode(null);
              setDialog({ mode: 'edit', member: m, initial: initialMemberValues(m) });
            }}
          />
        </div>
      )}

      <MemberFormDialog
        open={dialog != null}
        mode={dialog?.mode ?? 'create'}
        member={dialog?.mode === 'edit' ? dialog.member : undefined}
        memberRole={dialog?.mode === 'edit' ? roleFor(dialog.member) : undefined}
        initial={dialog?.mode === 'edit' ? dialog.initial : undefined}
        teams={data.teams}
        managerOptions={managerOptions}
        isPending={pending}
        onClose={() => setDialog(null)}
        onCreate={submitCreate}
        onUpdate={submitUpdate}
      />

      <MemberStatusDialog
        open={statusFor != null}
        member={statusFor}
        departureDate={statusFor ? (data.departureDates[norm(statusFor.email)] ?? null) : null}
        isPending={setEmployed.isPending}
        onClose={() => setStatusFor(null)}
        onConfirm={submitStatus}
      />
    </div>
  );
}

// ═══ File des demandes d'inactivation (P-ADM-17) ══════════════════════════════
// Porte du PoC inactivation-requests-panel.tsx : l'admin approuve (applique la
// mise en inactif + date de depart, transactionnel serveur) ou refuse (note).
// Une seule decision en vol a la fois ; le double-clic est un no-op serveur
// (idempotent) — la file se rafraichit par invalidation de la famille admin.

function InactivationQueue({
  requests,
  isDeciding,
  onDecide,
}: {
  requests: AdminInactivationRequest[];
  isDeciding: boolean;
  onDecide: (id: string, decision: 'approved' | 'rejected', note: string) => void;
}) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState<Record<string, string>>({});

  if (requests.length === 0) return null;

  return (
    <section className={`${GS_COMPONENTS.card} flex flex-col gap-4 p-5`}>
      <SectionHeader
        title={t('gest:admin.members.requests.title')}
        description={t('gest:admin.members.requests.description')}
      >
        <span className="text-chip text-slate-400 dark:text-slate-500">
          {t('gest:admin.members.requests.countPending', { n: requests.length })}
        </span>
      </SectionHeader>

      <ul className="flex flex-col gap-3">
        {requests.map((r) => {
          const name = r.targetName ?? r.targetEmail;
          return (
            <li
              key={r.id}
              className="flex flex-col gap-3 rounded-xl border border-slate-200 p-4 dark:border-slate-700"
            >
              <div className="flex flex-col gap-0.5">
                <span className="flex items-center gap-2 font-medium text-slate-800 dark:text-slate-100">
                  <UserMinus size={16} aria-hidden="true" />
                  {name}
                </span>
                <span className="text-chip text-slate-500 dark:text-slate-400">
                  {t('gest:admin.members.requests.requestedBy', {
                    name: r.requestedByName ?? r.targetEmail,
                    date: formatDateTimeFr(r.requestedAt),
                  })}
                  {' · '}
                  {t('gest:admin.members.requests.lastDay', {
                    date: formatDateOnlyFr(r.lastActiveDay),
                  })}
                </span>
                {r.reason ? (
                  <span className="text-field text-slate-500 dark:text-slate-400">
                    {'« '}{r.reason}{' »'}
                  </span>
                ) : null}
              </div>

              <input
                aria-label={t('gest:admin.members.requests.noteAria', { name })}
                placeholder={t('gest:admin.members.requests.notePlaceholder')}
                value={notes[r.id] ?? ''}
                onChange={(e) => setNotes((n) => ({ ...n, [r.id]: e.target.value }))}
                maxLength={500}
                className={GS_COMPONENTS.input}
              />

              <div className="flex flex-wrap justify-end gap-2">
                <button
                  type="button"
                  disabled={isDeciding}
                  onClick={() => onDecide(r.id, 'rejected', notes[r.id] ?? '')}
                  className={`${GS_COMPONENTS.btnSecondary} inline-flex touch-manipulation items-center gap-1.5 disabled:opacity-50`}
                >
                  <UserX size={14} aria-hidden="true" />
                  {t('gest:admin.members.requests.reject')}
                </button>
                <button
                  type="button"
                  disabled={isDeciding}
                  onClick={() => onDecide(r.id, 'approved', notes[r.id] ?? '')}
                  className={`${GS_COMPONENTS.btnAction} inline-flex touch-manipulation items-center gap-1.5 disabled:opacity-50`}
                >
                  <Check size={14} aria-hidden="true" />
                  {isDeciding
                    ? t('gest:admin.members.requests.deciding')
                    : t('gest:admin.members.requests.approve')}
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
